import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Clock3Icon, MenuIcon, MessageCircleIcon, PhoneIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { navItems } from '../../data/navigation';
import { site } from '../../data/site';
import { buttonStyles, container, focusRing } from '../../utils/styles';
import { Logo } from './Logo';
import { LanguageToggle } from './LanguageToggle';
export function Header() {
  const {
    t
  } = useLanguage();
  const [open, setOpen] = useState(false);
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => setOpen(false), [pathname, hash]);
  const isActive = (path: string) => {
    if (path.includes('#')) return `${pathname}${hash}` === path;
    if (path === '/') return pathname === '/' && hash !== '#how-it-works';
    return pathname.startsWith(path);
  };
  return <header className="sticky top-0 z-40 border-b border-mist bg-white" data-id="element-147" data-mp="3uejyqn7dv7kq">
      <div className="hidden bg-brand-deeper text-sm text-white sm:block" data-id="element-148" data-mp="6uvppki5flyc">
        <div className={`${container} flex h-12 items-center justify-between gap-4`} data-id="element-149" data-mp="btzvs29s13kg">
          <p className="hidden items-center gap-2 text-white/90 md:flex" data-id="element-150" data-mp="gkygde1ssnti">
            <Clock3Icon className="h-4 w-4" aria-hidden="true" data-id="element-151" data-mp="gnuh1a515qln" />
            {t({
            en: 'Open 24 hours · Se habla español',
            es: 'Abierto 24 horas · We speak English'
          })}
          </p>
          <div className="flex w-full items-center justify-between gap-3 md:w-auto md:justify-end" data-id="element-152" data-mp="3k4b94zsudmy0">
            <a href={site.tel} className={`flex items-center gap-2 rounded font-display text-base font-bold ${focusRing}`} data-id="element-153" data-mp="3p3e41rf3ycs9">
              <PhoneIcon className="h-4 w-4 text-gold" aria-hidden="true" data-id="element-154" data-mp="1wlq8xpe5z6x" />
              {site.phoneDisplay}
            </a>
            <div className="flex items-center gap-2" data-id="element-155" data-mp="19ax85h7dmz1v">
              <a href={site.tel} className={`inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-4 font-semibold text-brand-deeper transition-colors duration-150 ease-out hover:bg-cream ${focusRing}`} data-id="element-156" data-mp="19ci4ncd94s2d">
                {t({
                en: 'Call Now',
                es: 'Llamar'
              })}
              </a>
              <a href={site.whatsapp} target="_blank" rel="noopener noreferrer" className={`inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full bg-sage-dark px-4 font-semibold text-white transition-colors duration-150 ease-out hover:bg-sage-darker ${focusRing}`} data-id="element-157" data-mp="3jc0cryx2n5pt">
                <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-158" data-mp="30e78otapopqu" />
                {t({
                en: 'Text / WhatsApp',
                es: 'Texto / WhatsApp'
              })}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className={`${container} flex h-20 items-center justify-between gap-4`} data-id="element-159" data-mp="27xedz7pr9v18">
        <Logo data-id="element-160" data-mp="22yth9i9ppthj" />

        <nav className="hidden items-center gap-1 xl:flex" aria-label={t({
        en: 'Main',
        es: 'Principal'
      })} data-id="element-161" data-mp="22zcy0vnmmbtz">
          {navItems.map(item => {
          const active = isActive(item.path);
          return <Link key={item.path} to={item.path} aria-current={active ? 'page' : undefined} className={`whitespace-nowrap rounded-full px-3 py-2 text-[0.94rem] font-medium transition-colors duration-150 ease-out ${focusRing} ${active ? 'bg-brand-tint text-brand-dark' : 'text-ink hover:bg-cream hover:text-brand'}`} data-id="element-162" data-mp="n8izznnul6f2">
                {t(item.label)}
              </Link>;
        })}
        </nav>

        <div className="flex items-center gap-2" data-id="element-163" data-mp="s6c66gp5i0fc">
          <LanguageToggle className="hidden sm:inline-flex" data-id="element-164" data-mp="x55kgbc9hvgk" />
          <Link to="/contact" className={`${buttonStyles.primary} hidden md:inline-flex`} data-id="element-165" data-mp="x7rrm9v8q8y9">
            {t({
            en: 'Schedule Now',
            es: 'Agendar Ahora'
          })}
          </Link>
          <a href={site.tel} className={`flex h-12 w-12 items-center justify-center rounded-full bg-brand-tint text-brand-dark sm:hidden ${focusRing}`} aria-label={t({
          en: `Call ${site.phoneDisplay}`,
          es: `Llamar al ${site.phoneDisplay}`
        })} data-id="element-166" data-mp="3hwp8j5evjkxi">
            <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-167" data-mp="3oeah7ufxmhqn" />
          </a>
          <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? t({
          en: 'Close menu',
          es: 'Cerrar menú'
        }) : t({
          en: 'Open menu',
          es: 'Abrir menú'
        })} className={`flex h-12 w-12 items-center justify-center rounded-full border border-mist text-ink xl:hidden ${focusRing}`} data-id="element-168" data-mp="3jeo4rn7xh5iw">
            {open ? <XIcon className="h-6 w-6" data-id="element-169" data-mp="5yq7dh8s0xb3" /> : <MenuIcon className="h-6 w-6" data-id="element-170" data-mp="61m4528w1fxy" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-171" data-mp="2vmh5af6woif9">
        {open && <motion.nav id="mobile-menu" aria-label={t({
        en: 'Mobile',
        es: 'Móvil'
      })} initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute inset-x-0 top-full max-h-[calc(100vh-8rem)] overflow-y-auto border-b border-mist bg-white shadow-lg xl:hidden" data-id="element-172" data-mp="2qp7ht532ugu2">
            <div className={`${container} py-4`} data-id="element-173" data-mp="2vc9h67amuq8k">
              <ul className="divide-y divide-mist" data-id="element-174" data-mp="2zb05pq118zk2">
                {navItems.map(item => <li key={item.path} data-id="element-175" data-mp="34a3bjcou79t3">
                    <Link to={item.path} onClick={() => setOpen(false)} aria-current={isActive(item.path) ? 'page' : undefined} className={`flex min-h-[56px] items-center text-lg font-semibold ${focusRing} ${isActive(item.path) ? 'text-brand' : 'text-ink'}`} data-id="element-176" data-mp="34d7k0m9c4rpk">
                      {t(item.label)}
                    </Link>
                  </li>)}
              </ul>
              <div className="mt-4 flex items-center justify-between gap-3 sm:hidden" data-id="element-177" data-mp="16w0gev1whvn4">
                <span className="font-semibold text-ink-soft" data-id="element-178" data-mp="1lshv9w9atsef">{t({
                en: 'Language',
                es: 'Idioma'
              })}</span>
                <LanguageToggle data-id="element-179" data-mp="1gt58fbifglbu" />
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-180" data-mp="1gsdbnxd3ghjz">
                <Link to="/contact" className={`${buttonStyles.primary} w-full`} data-id="element-181" data-mp="1useaz8dpngxc">
                  {t({
                en: 'Schedule Free Consultation',
                es: 'Agendar Consulta Gratuita'
              })}
                </Link>
                <a href={site.tel} className={`${buttonStyles.outline} w-full`} data-id="element-182" data-mp="24i7mk9durw1l">
                  <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-183" data-mp="1zjmmc9ylpcd5" />
                  {site.phoneDisplay}
                </a>
              </div>
            </div>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}